import React, { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";

import { type LocaleOfStoreType } from "@beautypie/core";

import { ComparisonTable } from "./ComparisonTable";
import * as styles from "./ProductComparison.css";
import { getComparisonData } from "./ProductComparison.utils";

type Props = {
  slug: string;
  locale: LocaleOfStoreType;
  onElementViewed: () => void;
  onScroll?: () => void;
  currencySymbol: "£" | "$";
  title?: string;
  id?: string;
  footerCTA?: {
    copy: string;
    href: string;
    altText?: string;
  };
};

/**
 * A wrapper component that displays product comparison data in a table format.
 * Uses intersection observer to track when the component comes into view and triggers analytics.
 * Automatically fetches comparison data based on the product slug and handles empty states.
 *
 * @param slug - Product slug used to fetch comparison data from the utils function
 * @param locale - Locale for targeting the correct store data
 * @param onElementViewed - Callback function called when component enters viewport (for analytics tracking)
 * @param onScroll - Optional callback function called when user scrolls the comparison table (called only once per component mount)
 * @param title - Optional title displayed above the comparison table (defaults to "Comparison")
 * @param currencySymbol - Currency symbol for price formatting
 * @param id - Optional HTML id attribute for the main container div (defaults to "product-comparison")
 * @param footerCTA - Optional footer call-to-action object containing copy and href for the footer link
 * @returns {JSX.Element | null} The product comparison component or null if no comparison data exists
 *
 * @example
 * ```tsx
 * const handleElementViewed = () => {
 *   // Track analytics event
 *   analytics.track('product_comparison_viewed', { slug: 'mascara' });
 * };
 *
 * const handleScroll = () => {
 *   // Track scroll interaction
 *   analytics.track('product_comparison_scrolled', { slug: 'mascara' });
 * };
 *
 * <ProductComparison
 *   slug="mascara"
 *   locale="en-GB"
 *   title="Product Comparison"
 *   currencySymbol="£"
 *   onElementViewed={handleElementViewed}
 *   onScroll={handleScroll}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // US locale example with custom title
 * <ProductComparison
 *   slug="foundation"
 *   locale="en-US"
 *   title="Foundation Comparison"
 *   currencySymbol="$"
 *   onElementViewed={() => console.log('Product comparison viewed')}
 * />
 * ```
 */

export const ProductComparison: React.FC<Props> = ({
  locale,
  slug,
  onElementViewed,
  onScroll,
  title,
  currencySymbol,
  id = "product-comparison",
  footerCTA
}) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  const scrollableRef = useRef<HTMLDivElement>(null);

  const { inView, ref } = useInView({
    threshold: 1,
    triggerOnce: true
  });

  useEffect(() => {
    if (inView) {
      onElementViewed();
    }
  }, [inView, onElementViewed]);

  // Handle scroll tracking
  useEffect(() => {
    if (!onScroll || hasScrolled || !scrollableRef.current) return;

    const handleScroll = () => {
      if (!hasScrolled) {
        setHasScrolled(true);
        onScroll();
      }
    };

    const scrollableElement = scrollableRef.current;
    scrollableElement.addEventListener("scroll", handleScroll, {
      passive: true
    });

    return () => {
      scrollableElement.removeEventListener("scroll", handleScroll);
    };
  }, [onScroll, hasScrolled]);

  const comparisonData = getComparisonData(slug, locale);
  if (!comparisonData) return null;

  return (
    <div className={styles.container} id={id}>
      <ComparisonTable
        ref={ref}
        data={comparisonData}
        title={title}
        locale={locale}
        currencySymbol={currencySymbol}
        scrollableRef={scrollableRef}
        footerCTA={footerCTA}
      />
    </div>
  );
};
